// src/models/KbFaqCategoryModel.js
// ==========================================
// KbFaqCategoryModel
// Mirrors the `kbFaqCategories` collection document shape used by
// KnowledgeBaseService. One doc per category; FAQs are nested in `questions[]`.
// ==========================================

export class KbFaqCategoryModel {
  // ── Category-level keys ─────────────────────────────────────────
  static ALLOWED_KEYS = Object.freeze([
    "id",
    "name",
    "audience",        // "general" | "admin" | "auditor" | ...
    "companyId",
    "questions",       // ← nested FAQ array (validated per-item)
    "metadata",
  ]);

  // ── Metadata block (matches AuditHistoryModel) ─────────────────
  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "deletedFlag",
  ]);

  // ── Per-FAQ item keys ──────────────────────────────────────────
  static ALLOWED_ITEM_KEYS = Object.freeze([
    "id",
    "question",
    "answer",
    "tags",
    "metadata",        // optional per-item metadata (createdAt/updatedAt live here today)
  ]);

  static ID_FIELD   = "id";
  static NAME_FIELD = "name";

  // ── Item validator (static, reusable) ──────────────────────────
  // Returns { ok: true } or { ok: false, errors: [...] }
  static validateItem(item = {}, { isCreation = true } = {}) {
    const errors = [];

    // Drop unknown keys silently (defence against pollution)
    // but flag only missing/invalid required fields.
    const question = (item.question || "").trim();
    const answer   = (item.answer   || "").trim();

    if (!question)                     errors.push("question is required");
    if (question && question.length < 10)
      errors.push("question must be at least 10 characters");
    if (!answer)                       errors.push("answer is required");
    if (answer && answer.length < 20)
      errors.push("answer must be at least 20 characters");

    if (item.tags !== undefined && !Array.isArray(item.tags))
      errors.push("tags must be an array");

    // On creation the caller must NOT supply a duplicate of an existing id —
    // dedupe is handled by the service; here we only sanity-check shape.
    if (item.id !== undefined && typeof item.id !== "string")
      errors.push("id must be a string");

    return errors.length
      ? { ok: false, errors }
      : { ok: true };
  }

  // ── Constructor ────────────────────────────────────────────────
  constructor(data = {}, { isCreation = false } = {}) {
    // 1. Defaults for every category key
    for (const key of KbFaqCategoryModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt:   null,
          createdBy:   null,
          updatedAt:   null,
          updatedBy:   null,
          deletedFlag: false,
        };
      } else if (key === "questions") {
        this.questions = [];
      } else if (key === "audience") {
        this.audience = "general";
      } else if (key === "companyId") {
        this.companyId = null;
      } else {
        this[key] = key === "name" ? "" : null;
      }
    }

    if (isCreation) {
      // ── Creation path: accept top-level keys + snake/camel aliases ──
      this.id         = data.id         || crypto.randomUUID();
      this.name       = (data.name      || data.title || "").toString();
      this.audience   = data.audience   || "general";
      this.companyId  = data.companyId  || data.company_id || null;
      this.questions  = Array.isArray(data.questions)
        ? data.questions.map((q) => this._normalizeItem(q, { isCreation: true }))
        : [];

      const now = new Date().toISOString();
      this.metadata = {
        createdAt:   data.metadata?.createdAt   || now,
        createdBy:   data.metadata?.createdBy   || "system",
        updatedAt:   data.metadata?.updatedAt   || now,
        updatedBy:   data.metadata?.updatedBy   || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
      };
    } else {
      // ── Hydration path: only copy allowed keys ────────────────────
      const source = data.details || data;   // repo/Supabase unwrap

      for (const key of Object.keys(source)) {
        if (!KbFaqCategoryModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...this.metadata, ...(source.metadata || {}) };
        } else if (key === "questions") {
          this.questions = Array.isArray(source.questions)
            ? source.questions.map((q) => this._normalizeItem(q, { isCreation: false }))
            : [];
        } else {
          this[key] = source[key];
        }
      }

      // Fallbacks the service already relied on
      if (!this.id)   this.id   = data.id || null;
      if (!this.name) this.name = source.name || data.name || "";

      // Backward-compat: hoist legacy top-level timestamps into metadata
      if (data.createdAt) this.metadata.createdAt = data.createdAt;
      if (data.createdBy) this.metadata.createdBy = data.createdBy;
      if (data.updatedAt) this.metadata.updatedAt = data.updatedAt;
      if (data.updatedBy) this.metadata.updatedBy = data.updatedBy;
    }
  }

  // Normalise a nested FAQ item so it never carries unknown keys
  _normalizeItem(raw = {}, { isCreation = false } = {}) {
    const out = {};
    for (const k of KbFaqCategoryModel.ALLOWED_ITEM_KEYS) {
      if (k === "metadata") {
        out.metadata = {
          createdAt: raw.createdAt || raw.metadata?.createdAt || null,
          updatedAt: raw.updatedAt || raw.metadata?.updatedAt || null,
        };
      } else if (k === "tags") {
        out.tags = Array.isArray(raw.tags) ? raw.tags : [];
      } else {
        out[k] = raw[k] ?? null;
      }
    }
    if (isCreation) {
      if (!out.id) out.id = `faq_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      if (!out.metadata.createdAt) out.metadata.createdAt = new Date().toISOString();
    }
    // Trim strings for cleanliness
    out.question = (out.question || "").toString().trim();
    out.answer   = (out.answer   || "").toString().trim();
    return out;
  }

  // ── Validation ─────────────────────────────────────────────────
  isValid() {
    if (!this.id)        return false;
    if (!this.name || this.name.trim().length === 0) return false;
    if (!this.companyId) return false;
    if (!Array.isArray(this.questions)) return false;

    // Every item must be individually valid
    for (const q of this.questions) {
      const { ok } = KbFaqCategoryModel.validateItem(q, { isCreation: false });
      if (!ok) return false;
    }
    return true;
  }

  // ── Payload (flat — matches `_persistCategory`) ────────────────
  toPayload() {
    const payload = {};
    for (const key of KbFaqCategoryModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        payload.metadata = {};
        for (const mk of KbFaqCategoryModel.ALLOWED_METADATA_KEYS) {
          payload.metadata[mk] =
            this.metadata?.[mk] ?? (mk === "deletedFlag" ? false : null);
        }
      } else if (key === "questions") {
        payload.questions = this.questions.map((q) => ({
          id:       q.id,
          question: q.question,
          answer:   q.answer,
          tags:     Array.isArray(q.tags) ? q.tags : [],
          metadata: {
            createdAt: q.metadata?.createdAt ?? null,
            updatedAt: q.metadata?.updatedAt ?? null,
          },
        }));
      } else if (this[key] !== undefined) {
        payload[key] = this[key];
      }
    }
    return payload;
  }

  // ── Payload for Supabase-style `details` wrapper ───────────────
  // `_normalizeCategoryDoc` reads `doc.details || doc`, so if your repo
  // needs nested storage, call this instead of toPayload().
  toSupabasePayload() {
    const { id, ...rest } = this.toPayload();
    return { id, details: rest };
  }

  toJSON() {
    return this.toPayload();
  }
}